import { getAnalyticsPage } from "./page";

const privateProperties = new Set([
  "search",
  "hash",
  "title",
  "email",
  "name",
  "ph_keyword",
]);

function getBasePropertyName(key: string) {
  // PostHog copies attribution properties onto initial and session entry fields.
  return key
    .toLowerCase()
    .replace(/^\$/, "")
    .replace(/^(?:(?:initial|session_entry|prev_pageview)_)+/, "");
}

function parseAnalyticsUrl(value: string) {
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
    return url;
  } catch {
    return undefined;
  }
}

export function sanitizeAnalyticsPathname(value: string) {
  const pathname = value.split(/[?#]/)[0];
  const page = getAnalyticsPage(pathname);
  if (!page) return undefined;
  // Entity IDs are useful links; access tokens and unknown routes remain masked.
  return page.page_path === "/review/[token]" || page.page_path === "/unknown"
    ? page.page_path
    : pathname;
}

export function sanitizeAnalyticsUrl(value: string) {
  const url = parseAnalyticsUrl(value);
  if (!url) return undefined;
  const pathname = sanitizeAnalyticsPathname(url.pathname);
  return pathname ? `${url.origin}${pathname}` : undefined;
}

function sanitizeReferrerUrl(value: string, merchantOrigin: string) {
  if (value === "$direct") return value;
  const url = parseAnalyticsUrl(value);
  if (!url) return undefined;
  if (url.origin === merchantOrigin) return sanitizeAnalyticsUrl(value);
  // External paths can contain private identifiers; the origin retains site attribution.
  return url.origin;
}

function sanitizeProperties(
  properties: Record<string, unknown>,
  merchantOrigin: string,
  preserveProperty: (key: string) => boolean,
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(properties).flatMap(([key, value]) => {
      if (preserveProperty(key)) return [[key, value]];
      const baseName = getBasePropertyName(key);
      if (privateProperties.has(baseName)) return [];
      if (
        ["$set", "$set_once"].includes(key) &&
        value &&
        typeof value === "object" &&
        !Array.isArray(value)
      ) {
        return [
          [
            key,
            sanitizeAnalyticsPersonProperties(
              Object.fromEntries(Object.entries(value)),
              merchantOrigin,
            ),
          ],
        ];
      }
      if (baseName.endsWith("referrer") && typeof value === "string") {
        const sanitized = sanitizeReferrerUrl(value, merchantOrigin);
        return sanitized ? [[key, sanitized]] : [];
      }
      if (baseName.endsWith("url") && typeof value === "string") {
        const sanitized = sanitizeAnalyticsUrl(value);
        return sanitized ? [[key, sanitized]] : [];
      }
      if (baseName.endsWith("pathname") && typeof value === "string") {
        const pathname = sanitizeAnalyticsPathname(value);
        return pathname ? [[key, pathname]] : [];
      }
      return [[key, value]];
    }),
  );
}

export function sanitizeAnalyticsEventProperties(
  eventName: string,
  properties: Record<string, unknown>,
  merchantOrigin: string,
) {
  // Existing reports consume the authenticated email on the legacy pageview event.
  return sanitizeProperties(
    properties,
    merchantOrigin,
    (key) => eventName === "app_page_view" && key === "email",
  );
}

export function sanitizeAnalyticsPersonProperties(
  properties: Record<string, unknown>,
  merchantOrigin: string,
) {
  return sanitizeProperties(
    properties,
    merchantOrigin,
    (key) => key === "email" || key === "name",
  );
}
